/**
 * Appointment-duplicate CONFIRMATION service — CLIENT side (Jose 2026-10-20).
 *
 * The Fresha → SLIK migration left some clients with TWO copies of one upcoming appointment (an
 * online booking they made + a Fresha import of the same slot, or a double Fresha export). We text
 * them a /confirm/:token link; this service reads that confirmation by token and lets them tap the
 * ONE that's real. Tapping flips only the resolve fields on the confirmation doc — a Cloud Function
 * trigger (onDuplicateConfirmationResolved) then cancels the losing copy server-side (clients can't
 * write appointments directly). Mirrors the waitlist-offer claim flow.
 */
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

const COLLECTION = 'apptConfirmations';

export interface ConfirmCandidate {
  apptId: string;
  date: string;      // 'YYYY-MM-DD'
  time: string;      // '2:00 PM' or '14:30'
  barberName: string;
  service: string;
}

export interface ApptConfirmation {
  token: string;
  clientName: string;
  clientPhone?: string;
  candidates: ConfirmCandidate[];
  status: 'pending' | 'resolved';
  keptApptId?: string;
}

function map(id: string, data: any): ApptConfirmation {
  return {
    token: data.token || id,
    clientName: data.clientName || '',
    clientPhone: data.clientPhone || '',
    candidates: Array.isArray(data.candidates) ? data.candidates : [],
    status: data.status || 'pending',
    keptApptId: data.keptApptId,
  };
}

class ApptConfirmationService {
  /** Load a confirmation by its token (== doc id). Null if it doesn't exist. */
  async getByToken(token: string): Promise<ApptConfirmation | null> {
    const snap = await getDoc(doc(db, COLLECTION, token));
    if (!snap.exists()) return null;
    return map(snap.id, snap.data());
  }

  /**
   * Mark which appointment is the real one. We only flip the resolve fields; the server trigger
   * cancels the OTHER candidate(s). Idempotent — re-tapping the same choice is a no-op.
   */
  async resolve(token: string, keepApptId: string): Promise<void> {
    await updateDoc(doc(db, COLLECTION, token), {
      status: 'resolved',
      keptApptId: keepApptId,
      resolvedAtMs: Date.now(),
    });
  }
}

export const apptConfirmationService = new ApptConfirmationService();
export default apptConfirmationService;
